import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { componentFactoryOf } from 'vue-tsx-support';
import { Vue } from 'vue/types/vue';

import { JtnUiDefaultBtn } from '@jtnews/jtn-ui';
import {
  injectStylesMixin,
  tsStoreMixin,
  DESKTOP_HEADER_HEIGHT,
  MOBILE_HEADER_HEIGHT
} from '@jtnews/shared';
import { scrollToElement } from '@jtnews/shared/scroll-and-position';

import { RecordPost, RecordCards } from '../../../../shared/models/record';
import Post from '../broadcast-post/index.vue';
import Card from '../card/index.vue';

import styles from './scrollable-content.styles.scss?module';

interface Events {
  onScrollToContent: void;
}

type Content = RecordCards | RecordPost;

type Point = {
  id: number;
  navigatedBy: 'scroll' | 'click';
};

type ComponentData = {
  currentContentIndex: null | number;
  currentContentId: number;
  needCheckFigureSize: boolean;
  scrollTimeout: number;
  destroy$: Subject<void>;
};

export default componentFactoryOf<Events>()
  .mixin(injectStylesMixin(styles))
  .mixin(tsStoreMixin)
  .create({
    name: 'ScrollableContent',
    props: {
      contentList: {
        type: Array as () => Content[],
        required: true
      },
      label: {
        type: String as () => 'Показать предыдущие' | 'Показать следующие',
        default: 'Показать предыдущие'
      },
      sortType: {
        type: String as () => '' | 'asc' | 'desc',
        default: ''
      },
      type: {
        type: String as () => 'card' | 'post',
        default: 'card'
      }
    },
    data(): ComponentData {
      return {
        currentContentIndex: null,
        currentContentId: 0,
        needCheckFigureSize: false,
        scrollTimeout: 0,
        destroy$: new Subject()
      };
    },
    watch: {
      sortType(value: string, oldValue: string) {
        if (value && oldValue && this.currentContentIndex !== 0) {
          this.scrollToPoint(this.contentList[0].id);
        }
      }
    },
    computed: {
      headerHeight(): number {
        return this.store.deviceInfo.isMobile && !this.store.deviceInfo.isTablet
          ? MOBILE_HEADER_HEIGHT
          : DESKTOP_HEADER_HEIGHT;
      }
    },
    mounted() {
      this.showCurrentContent();
      this.store.layoutModule.currentPointId$
        .pipe(
          takeUntil(this.destroy$),
          filter(({ id }: Point): boolean => id !== this.currentContentId)
        )
        .subscribe(({ id, navigatedBy }: Point) => {
          if (navigatedBy === 'click') {
            this.scrollToPoint(id);
            this.$emit('scrollToContent');
          } else {
            this.currentContentId = id;
          }
        });
    },
    beforeDestroy() {
      clearTimeout(this.scrollTimeout);

      this.destroy$.next();
      this.destroy$.unsubscribe();
    },
    methods: {
      setCurrentPoint({ id, navigatedBy }: Point) {
        this.store.layoutModule.setCurrentPointId({ id, navigatedBy });
      },
      showCurrentContent() {
        const { query } = this.$route;
        let contentId: number;
        if (query?.sharePost) {
          contentId = Number(query.sharePost);
        } else if (query?.shareCard) {
          contentId = Number(query.shareCard);
        } else {
          const { hash } = this.$route;
          contentId = Number(hash.split(`#${this.type}_`)[1]);
        }

        if (contentId) {
          const contentIndex = this.contentList.findIndex(
            ({ id }: Content) => id === contentId
          );

          this.currentContentIndex = contentIndex !== -1 ? contentIndex : 0;
          this.scrollToContent(this.contentList[this.currentContentIndex].id, 3000);
          this.$emit('scrollToContent');
        } else {
          this.currentContentIndex = 0;
        }
      },
      scrollToContent(contentId: number, timeout: number) {
        clearTimeout(this.scrollTimeout);

        this.currentContentId = contentId;
        this.scrollTimeout = window.setTimeout(() => {
          const element = (this.$refs[`${this.type}${contentId}`] as Vue)
            .$el as HTMLElement;
          scrollToElement(element, 500, { up: this.headerHeight });
        }, timeout);
      },
      scrollToPoint(id: number) {
        if (this.currentContentIndex !== 0) {
          this.currentContentIndex = 0;
          this.needCheckFigureSize = true;
        }

        this.scrollToContent(id, 500);
      }
    },
    render() {
      const COMPONENT = this.type === 'card' ? Card : Post;

      return (
        <div>
          <JtnUiDefaultBtn
            class={[
              styles['show-button'],
              this.currentContentIndex ? styles.visible : null
            ]}
            btnType="button-scrollable-content"
            onClick={() => this.scrollToPoint(this.contentList[0].id)}
          >
            {this.label}
          </JtnUiDefaultBtn>

          {this.contentList.map((content, index: number) => (
            <COMPONENT
              key={content.id + index}
              ref={`${this.type}${content.id}`}
              content={content}
              headerHeight={this.headerHeight}
              number={index + 1}
              isCurrent={this.currentContentId === content.id}
              isHidden={
                this.currentContentIndex === null || this.currentContentIndex > index
              }
              needCheckFigureSize={this.needCheckFigureSize}
              onPointActive={(event: Point) => this.setCurrentPoint(event)}
            >
              <template slot="adv">{this.$scopedSlots.adv({ index })}</template>
            </COMPONENT>
          ))}
        </div>
      );
    }
  });
